import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Camera, MapPin, Clock, LogIn, LogOut, RotateCcw, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { getUser } from '@/lib/auth';
import api from '@/lib/api';

export default function MobileClockPage() {
  const user = getUser();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState(null); // from /my-status
  const [punches, setPunches] = useState(null); // from /my-punches
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [faceDetected, setFaceDetected] = useState(null); // null=pending, true, false
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  // Update clock every second
  useEffect(() => {
    const tick = () => {
      setCurrentTime(new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get('/api/attendance/my-status'),
        api.get('/api/attendance/my-punches'),
      ]);
      setStatus(s.data);
      setPunches(p.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // GPS
  const getGPS = useCallback(() => {
    setGpsLoading(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      setGps({ lat: 0, lng: 0 });
      setGpsLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsLoading(false);
      },
      () => {
        toast.error('Location access denied. Using default.');
        setGps({ lat: 0, lng: 0 });
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Camera
  const openCamera = async () => {
    setCapturedImage(null);
    setFaceDetected(null);
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      toast.error('Camera access denied');
      setCameraOpen(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    // Mirror for selfie
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
    setCapturedImage(dataUrl);
    stopCamera();

    // Face detection
    await detectFace(canvas);
  };

  const detectFace = async (canvas) => {
    setFaceDetected(null);
    try {
      if ('FaceDetector' in window) {
        const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
        const faces = await detector.detect(canvas);
        setFaceDetected(faces.length > 0);
        if (faces.length === 0) toast.error('No face detected. Please retake.');
      } else {
        // Fallback: check center region has content (basic heuristic)
        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;
        const centerData = ctx.getImageData(w * 0.25, h * 0.15, w * 0.5, h * 0.7);
        const pixels = centerData.data;
        let variance = 0;
        let avg = 0;
        const step = 16;
        for (let i = 0; i < pixels.length; i += step * 4) {
          avg += pixels[i] + pixels[i + 1] + pixels[i + 2];
        }
        avg /= (pixels.length / (step * 4)) * 3;
        for (let i = 0; i < pixels.length; i += step * 4) {
          const v = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
          variance += (v - avg) * (v - avg);
        }
        variance /= (pixels.length / (step * 4));
        // If variance is low, likely blank/no face
        const hasFace = variance > 200;
        setFaceDetected(hasFace);
        if (!hasFace) toast.error('No face detected. Please retake.');
      }
    } catch {
      // If detection fails, allow submission with warning
      setFaceDetected(true);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setFaceDetected(null);
    openCamera();
  };

  // Submit punch
  const handlePunch = async (type) => {
    if (!capturedImage) { toast.error('Please take a selfie first'); return; }
    if (faceDetected === false) { toast.error('No face detected. Please retake the selfie.'); return; }
    if (!gps) { toast.error('Getting your location...'); getGPS(); return; }

    setSubmitting(true);
    try {
      const endpoint = type === 'in' ? '/api/attendance/clock-in' : '/api/attendance/clock-out';
      await api.post(endpoint, {
        selfie: capturedImage,
        lat: gps.lat,
        lng: gps.lng,
      });
      toast.success(type === 'in' ? 'Clocked in successfully!' : 'Clocked out successfully!');
      setCapturedImage(null);
      setFaceDetected(null);
      setGps(null);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  // Start flow: get GPS when page loads
  useEffect(() => { getGPS(); }, [getGPS]);

  const isClockedIn = status?.is_clocked_in || false;
  const punchAction = isClockedIn ? 'out' : 'in';
  const totalHours = punches?.total_hours || 0;
  const punchList = punches?.punches || [];

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="mobile-clock-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-xl font-bold text-[#0B1B33]">Attendance</h1>
        <p className="text-3xl font-mono font-bold text-[#0D2850] mt-1" data-testid="live-clock">{currentTime}</p>
        <p className="text-xs text-slate-500 mt-1">{new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* Status Badge */}
      {!loading && (
        <div className="flex justify-center">
          <Badge
            className={`px-4 py-1.5 text-sm font-semibold ${
              isClockedIn
                ? 'bg-green-100 text-green-700 border-green-300'
                : status?.record
                  ? 'bg-amber-100 text-amber-700 border-amber-300'
                  : 'bg-slate-100 text-slate-600 border-slate-300'
            }`}
            variant="outline"
            data-testid="clock-status-badge"
          >
            {isClockedIn ? 'Currently Working' : status?.record ? 'Clocked Out' : 'Not Clocked In'}
          </Badge>
        </div>
      )}

      {/* Working Hours Card */}
      {punches && punchList.length > 0 && (
        <Card className="border-slate-200 bg-gradient-to-r from-[#0D2850] to-[#162d4a]">
          <CardContent className="p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-white/60 uppercase tracking-wider">Today's Working Hours</p>
                <p className="text-2xl font-bold mt-0.5" data-testid="total-hours">{totalHours}h</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/60">Punches</p>
                <p className="text-lg font-bold" data-testid="punch-count">{punchList.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Camera Section */}
      <Card className="border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          {cameraOpen && !capturedImage && (
            <div className="relative bg-black">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-64 object-cover"
                style={{ transform: 'scaleX(-1)' }}
                data-testid="camera-preview"
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-40 h-48 border-2 border-white/40 rounded-2xl" />
              </div>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
                <Button
                  onClick={capturePhoto}
                  className="bg-white text-[#0D2850] hover:bg-white/90 rounded-full w-14 h-14 p-0 shadow-lg"
                  data-testid="capture-btn"
                >
                  <Camera className="w-6 h-6" />
                </Button>
                <Button
                  onClick={stopCamera}
                  variant="ghost"
                  className="text-white hover:bg-white/20 rounded-full w-14 h-14 p-0"
                  data-testid="close-camera-btn"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
            </div>
          )}

          {capturedImage && (
            <div className="relative">
              <img src={capturedImage} alt="Selfie" className="w-full h-64 object-cover" data-testid="captured-selfie" />
              {faceDetected === true && (
                <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1.5">
                  <CheckCircle className="w-4 h-4" />
                </div>
              )}
              {faceDetected === false && (
                <div className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5">
                  <AlertTriangle className="w-4 h-4" />
                </div>
              )}
              <Button
                onClick={retakePhoto}
                variant="outline"
                size="sm"
                className="absolute bottom-2 right-2 bg-white/90 text-xs gap-1"
                data-testid="retake-btn"
              >
                <RotateCcw className="w-3 h-3" /> Retake
              </Button>
            </div>
          )}

          {!cameraOpen && !capturedImage && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Camera className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500 mb-3">Take a selfie to {punchAction === 'in' ? 'clock in' : 'clock out'}</p>
              <Button
                onClick={openCamera}
                className="bg-[#0D2850] hover:bg-[#0B2246] text-white gap-2"
                data-testid="open-camera-btn"
              >
                <Camera className="w-4 h-4" /> Open Camera
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <canvas ref={canvasRef} className="hidden" />

      {/* GPS Info */}
      <Card className="border-slate-200">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${gps && gps.lat !== 0 ? 'bg-green-100' : 'bg-slate-100'}`}>
              <MapPin className={`w-4 h-4 ${gps && gps.lat !== 0 ? 'text-green-600' : 'text-slate-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              {gpsLoading ? (
                <p className="text-sm text-slate-500">Getting location...</p>
              ) : gps && gps.lat !== 0 ? (
                <div>
                  <p className="text-xs text-slate-500">Location captured</p>
                  <p className="text-xs font-mono text-slate-600 truncate">{gps.lat.toFixed(6)}, {gps.lng.toFixed(6)}</p>
                </div>
              ) : gps ? (
                <p className="text-sm text-amber-600">Location unavailable (permission denied)</p>
              ) : (
                <p className="text-sm text-slate-500">Location not available</p>
              )}
            </div>
            {gps && gps.lat !== 0 && (
              <a
                href={`https://www.google.com/maps?q=${gps.lat},${gps.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 underline whitespace-nowrap"
                data-testid="view-map-link"
              >
                View Map
              </a>
            )}
            {!gps && !gpsLoading && (
              <Button variant="outline" size="sm" className="text-xs" onClick={getGPS} data-testid="retry-gps-btn">
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Punch Button */}
      <Button
        onClick={() => handlePunch(punchAction)}
        disabled={submitting || !capturedImage || faceDetected === false || loading}
        className={`w-full h-14 text-base font-bold rounded-xl shadow-lg gap-2 ${
          punchAction === 'in'
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-red-600 hover:bg-red-700 text-white'
        }`}
        data-testid="punch-btn"
      >
        {submitting ? 'Submitting...' : (
          <>
            {punchAction === 'in' ? <LogIn className="w-5 h-5" /> : <LogOut className="w-5 h-5" />}
            {punchAction === 'in' ? 'Clock In' : 'Clock Out'}
          </>
        )}
      </Button>

      {faceDetected === false && (
        <p className="text-xs text-red-500 text-center -mt-2" data-testid="face-warning">
          Face not detected. Please retake the selfie with your face clearly visible.
        </p>
      )}

      {/* Punch Timeline */}
      {punchList.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-[#0B1B33] mb-3">Today's Punch Log</h3>
            <div className="space-y-3">
              {punchList.map((p, i) => (
                <div key={p.id || i} className="flex items-center gap-3" data-testid={`punch-entry-${i}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    p.type === 'in' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {p.type === 'in' ? (
                      <LogIn className="w-4 h-4 text-green-600" />
                    ) : (
                      <LogOut className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">
                      {p.type === 'in' ? 'Clock In' : 'Clock Out'}
                      {p.auto && <span className="text-xs text-amber-600 ml-2">(Auto)</span>}
                    </p>
                    <p className="text-xs text-slate-500">{p.time}</p>
                  </div>
                  {p.gps && p.gps.lat !== 0 && (
                    <a
                      href={`https://www.google.com/maps?q=${p.gps.lat},${p.gps.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
